import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, Check, X } from 'lucide-react';
import { Button } from '../ui/Button';
import { FormErrorMessage } from '../ui/FormErrorMessage';
import { FormSuccessMessage } from '../ui/FormSuccessMessage';
import { FormLoadingState } from '../ui/FormLoadingState';
import { BaseAccountForm } from './BaseAccountForm';
import { validateUsername } from '../../utils/accountValidation';
import { mobileDebounce } from '../../utils/debounce';
import { useAuth } from '../../hooks/useAuth';

export interface UsernameChangeFormProps {
  currentUsername: string;
  onSuccess: (newUsername: string) => void;
  onError: (error: string) => void;
}

interface UsernameChangeFormState {
  newUsername: string;
  isSubmitting: boolean;
  validationError: string | null;
  availabilityStatus: 'checking' | 'available' | 'taken' | null;
  successMessage: string | null;
}

export const UsernameChangeForm: React.FC<UsernameChangeFormProps> = ({
  currentUsername,
  onSuccess,
  onError
}) => {
  const { updateUsername, checkUsernameAvailability } = useAuth();
  
  const [state, setState] = useState<UsernameChangeFormState>({
    newUsername: currentUsername,
    isSubmitting: false,
    validationError: null,
    availabilityStatus: null,
    successMessage: null
  });

  // Debounced username availability check
  // useMemo ensures the debounced function is created once and remains stable,
  // preserving debounce behavior across renders
  const checkAvailability = useMemo(
    () => mobileDebounce(async (username: string) => {
      if (!username || username === currentUsername) {
        setState(prev => ({ ...prev, availabilityStatus: null }));
        return;
      }

      // Validate username format first
      const validation = validateUsername(username);
      if (!validation.isValid) {
        setState(prev => ({
          ...prev,
          availabilityStatus: null,
          validationError: validation.error || null
        }));
        return;
      }

      setState(prev => ({
        ...prev,
        availabilityStatus: 'checking',
        validationError: null
      }));

      try {
        const isAvailable = await checkUsernameAvailability(username);
        setState(prev => ({
          ...prev,
          availabilityStatus: isAvailable ? 'available' : 'taken'
        }));
      } catch (error) {
        console.error('Error checking username availability:', error);
        setState(prev => ({
          ...prev,
          availabilityStatus: null,
          validationError: 'Failed to check username availability'
        }));
      }
    }, 500),
    [checkUsernameAvailability, currentUsername]
  );

  // Handle username input change
  const handleUsernameChange = (value: string) => {
    setState(prev => ({ 
      ...prev, 
      newUsername: value,
      validationError: null,
      successMessage: null
    }));
    
    // Trigger availability check
    checkAvailability(value);
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedUsername = state.newUsername.trim();
    
    // Clear previous messages
    setState(prev => ({ 
      ...prev, 
      validationError: null, 
      successMessage: null 
    }));

    // Validate username
    const validation = validateUsername(trimmedUsername);
    if (!validation.isValid) {
      setState(prev => ({ ...prev, validationError: validation.error || null }));
      onError(validation.error || 'Validation failed');
      return;
    }

    // Check if username is the same as current
    if (trimmedUsername === currentUsername) {
      setState(prev => ({ 
        ...prev, 
        validationError: 'New username must be different from current username' 
      }));
      onError('New username must be different from current username');
      return;
    }

    // Check availability status
    if (state.availabilityStatus === 'taken') {
      setState(prev => ({ 
        ...prev, 
        validationError: 'Username is already taken' 
      }));
      onError('Username is already taken');
      return;
    }

    if (state.availabilityStatus === 'checking') {
      setState(prev => ({ 
        ...prev, 
        validationError: 'Please wait for availability check to complete' 
      }));
      onError('Please wait for availability check to complete');
      return;
    }

    setState(prev => ({ ...prev, isSubmitting: true }));

    try {
      const { error } = await updateUsername(trimmedUsername);
      
      if (error) {
        throw new Error(error.message || 'Failed to update username');
      }

      setState(prev => ({ 
        ...prev, 
        successMessage: 'Username updated successfully!',
        isSubmitting: false
      }));
      
      onSuccess(trimmedUsername);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to update username';
      setState(prev => ({ 
        ...prev, 
        validationError: errorMessage,
        isSubmitting: false
      }));
      onError(errorMessage);
    }
  };

  // Reset form when currentUsername changes
  useEffect(() => {
    setState(prev => ({
      ...prev,
      newUsername: currentUsername,
      validationError: null,
      successMessage: null,
      availabilityStatus: null
    }));
  }, [currentUsername]);

  const renderAvailabilityIndicator = () => {
    if (!state.newUsername || state.newUsername === currentUsername) {
      return null;
    }

    switch (state.availabilityStatus) {
      case 'checking':
        return (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Checking availability...</span>
          </div>
        );
      case 'available':
        return (
          <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
            <Check className="h-4 w-4" />
            <span>Username is available</span>
          </div>
        );
      case 'taken':
        return (
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <X className="h-4 w-4" />
            <span>Username is already taken</span>
          </div>
        );
      default:
        return null;
    }
  };

  const isFormValid = () => {
    const trimmedUsername = state.newUsername.trim();
    return (
      trimmedUsername &&
      trimmedUsername !== currentUsername &&
      !state.validationError &&
      state.availabilityStatus === 'available' &&
      !state.isSubmitting
    );
  };

  if (state.isSubmitting) {
    return (
      <FormLoadingState isLoading={true} loadingText="Updating username...">
        <div />
      </FormLoadingState>
    );
  }

  return (
    <BaseAccountForm
      title="Change Username"
      description="Update your display name. This will be visible to other users."
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <label 
            htmlFor="current-username"
            className="block text-sm sm:text-base font-medium text-foreground"
          >
            Current Username
          </label>
          <input
            id="current-username"
            type="text"
            value={currentUsername}
            disabled
            readOnly
            className="w-full px-3 py-2 sm:py-3 border rounded-md shadow-sm text-base sm:text-sm mobile-touch-target focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed bg-background text-foreground placeholder:text-muted-foreground"
          />
        </div>
        
        <div className="space-y-2">
          <label 
            htmlFor="new-username"
            className="block text-sm sm:text-base font-medium text-foreground"
          >
            New Username
            <span className="text-red-500 ml-1">*</span>
          </label>
          <input
            id="new-username"
            type="text"
            value={state.newUsername}
            onChange={(e) => handleUsernameChange(e.target.value)}
            placeholder="Enter new username"
            maxLength={20}
            required
            disabled={state.isSubmitting}
            className="w-full px-3 py-2 sm:py-3 border rounded-md shadow-sm text-base sm:text-sm mobile-touch-target focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed bg-background text-foreground placeholder:text-muted-foreground"
          />
          
          {renderAvailabilityIndicator()}
        </div>

        {state.validationError && (
          <FormErrorMessage error={state.validationError} />
        )}

        {state.successMessage && (
          <FormSuccessMessage message={state.successMessage} />
        )}

        <div className="flex gap-3 pt-4">
          <Button
            type="submit"
            disabled={!isFormValid()}
            className="flex-1 mobile-touch-target text-sm sm:text-base"
          >
            {state.isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Updating...
              </>
            ) : (
              'Update Username'
            )}
          </Button>
        </div>
      </div>
    </BaseAccountForm>
  );
};

export default UsernameChangeForm;